import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/axios/api-service';
import { ENDPOINTS } from '../services/endpoints';
import { DATE_RANGES } from '../shared/appConstants';
import { getDateRangeFromTimeframe } from '../shared/commonFunctions';

export const fetchBreakdownStatus = createAsyncThunk(
  'strategist/fetchBreakdownStatus',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${ENDPOINTS.AGENT_BREAKDOWN}?project_id=${projectId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch breakdown status');
    }
  }
);

export const createBreakdown = createAsyncThunk(
  'strategist/createBreakdown',
  async ({ projectId, requirement, customHierarchy }, { rejectWithValue }) => {
    try {
      const response = await api.post(ENDPOINTS.AGENT_BREAKDOWN, {
        requirement,
        customHierarchy
      }, {
        params: { project_id: projectId }
      });
      return response.data;
    } catch (error) {
      const apiError = error.response?.data?.error || error.response?.data?.message || error.message;
      return rejectWithValue(apiError);
    }
  }
);

export const generateTask = createAsyncThunk(
  'strategist/generateTask',
  async ({ projectId, ticketId }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `${ENDPOINTS.AGENT_GENERATE_TASK}?project_id=${projectId}`,
        {
          ticket_id: ticketId,
        }
      );
      return response.data;
    } catch (error) {
      const apiError = error.response?.data?.error || error.response?.data?.message || error.message;
      return rejectWithValue(apiError);
    }
  }
);

export const fetchHistory = createAsyncThunk(
  'strategist/fetchHistory',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.projects && params.projects.length > 0) {
        queryParams.append('projects', params.projects.join(','));
      }
      
      if (params.type) {
        queryParams.append('type', params.type);
      }
      
      if (params.page) {
        queryParams.append('page', params.page);
      }
      
      if (params.limit) {
        queryParams.append('limit', params.limit);
      }
      
      const url = `${ENDPOINTS.AGENT_HISTORY}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchAC = createAsyncThunk(
  'strategist/fetchAC',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${ENDPOINTS.AGENT_AC}?project_id=${projectId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch AC data');
    }
  }
);

export const updateAgentOutput = createAsyncThunk(
  'strategist/updateAgentOutput',
  async ({ agentLogId, agentOutput }, { rejectWithValue }) => {
    try {
      const response = await api.put(`${ENDPOINTS.AGENT_OUTPUT_UPDATE}?agent_log_id=${agentLogId}`, {
        agent_output: agentOutput
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createTickets = createAsyncThunk(
  'strategist/createTickets',
  async ({ projectId, tickets, breakdownHistoryId }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${ENDPOINTS.AGENT_BREAKDOWN}/tickets`, {
        tickets,
        breakdown_history_id: breakdownHistoryId,
      }, {
        params: { project_id: projectId }
      });
      return response.data;
    } catch (error) {
      const apiError = error.response?.data?.error || error.response?.data?.message || error.message;
      return rejectWithValue(apiError);
    }
  }
);
export const createAC = createAsyncThunk(
  'strategist/createAC',
  async ({ projectId, taskId, format, includeEdgeCases }, { rejectWithValue }) => {
    try {
      // Map format to API expected values
      const apiFormat = format === 'bullet' ? 'bullet points' : format;
      
      const response = await api.post(ENDPOINTS.AGENT_AC, {
        taskId,
        format: apiFormat,
        includeEdgeCases
      }, {
        params: { project_id: projectId }
      });
      
      // Check if API returned an error in the response body
      if (response.data && response.data.status === false) {
        const apiError = response.data.error || response.data.message;
        return rejectWithValue(apiError);
      }
      
      return { success: true };
    } catch (error) {
      const apiError = error.response?.data?.error || error.response?.data?.message || error.message;
      return rejectWithValue(apiError);
    }
  }
);

// This thunk is used to fetch the latest task generation history item for a given project. 
// It calls the same endpoint as fetchHistory but with parameters set to get only the latest item.
export const fetchTaskGeneratorLatest = createAsyncThunk(
  'strategist/fetchTaskGeneratorLatest',
  async (projectId, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ type: 'taskgeneration', page: 1, limit: 1 });
      if (projectId) params.append('projects', projectId);
      const response = await api.get(`${ENDPOINTS.AGENT_HISTORY}?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const generateReport = createAsyncThunk(
  'strategist/generateReport',
  async ({ projectId, reportType, options }, { rejectWithValue }) => {
    try {
      // Map UI options to API payload
      let from_date = null;
      let to_date = null;

      if (options.timeframe === DATE_RANGES.CUSTOM && options.customDateFrom && options.customDateTo) {
        from_date = options.customDateFrom;
        to_date = options.customDateTo;
      } else if (options.timeframe && options.timeframe !== DATE_RANGES.SELECT_TIME) {
        const range = getDateRangeFromTimeframe(options.timeframe);
        from_date = range.from_date;
        to_date = range.to_date;
      }

      const payload = {
        report_type: reportType,
        sprint_id: options.sprintId || null,
        from_date,
        to_date,
      };

      const response = await api.post(
        ENDPOINTS.AGENT_REPORT,
        payload,
        {
          params: { project_id: projectId },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message
      );
    }
  }
);

const initialState = {
  breakdown: null,
  breakdownStatus: null,
  acceptanceCriteria: null,
  loading: {
    fetchBreakdownStatus: false,
    createBreakdown: false,
    generateTask: false,
    updateAgentOutput: false,
    fetchAC: false,
    createAC: false,
    createTickets: false,
    fetchHistory: false,
    fetchTaskGeneratorLatest: false,
    generateReport: false,
  },
  error: null,
  history: {
    data: [],
    totalPages: 1,
    totalItems: 0,
    currentPage: 1,
  },
  taskGeneratorLatest: null,
  reports: {},
};

const strategistSlice = createSlice({
  name: 'strategist',
  initialState,
  reducers: {
    clearBreakdown: (state) => {
      state.breakdown = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearAC: (state) => {
      state.acceptanceCriteria = null;
      state.error = null;
    },
    clearHistory: (state) => {
      state.history = {
        data: [],
        totalPages: 1,
        totalItems: 0,
        currentPage: 1,
      };
    },
    clearReports: (state) => {
      state.reports = {};
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBreakdownStatus.pending, (state) => {
        state.loading.fetchBreakdownStatus = true;
        state.error = null;
      })
      .addCase(fetchBreakdownStatus.fulfilled, (state, action) => {
        state.loading.fetchBreakdownStatus = false;
        state.breakdownStatus = action.payload;
      })
      .addCase(fetchBreakdownStatus.rejected, (state, action) => {
        state.loading.fetchBreakdownStatus = false;
        state.error = action.payload;
      })
      .addCase(createBreakdown.pending, (state) => {
        state.loading.createBreakdown = true;
        state.error = null;
      })
      .addCase(createBreakdown.fulfilled, (state, action) => {
        state.loading.createBreakdown = false;
        state.breakdown = action.payload;
      })
      .addCase(createBreakdown.rejected, (state, action) => {
        state.loading.createBreakdown = false;
        state.error = action.payload;
      })
      .addCase(generateTask.pending, (state) => {
        state.loading.generateTask = true;
        state.error = null;
      })
      .addCase(generateTask.fulfilled, (state, action) => {
        state.loading.generateTask = false;
        state.breakdownStatus = action.payload;
      })
      .addCase(generateTask.rejected, (state, action) => {
        state.loading.generateTask = false;
        state.error = action.payload;
      })
      .addCase(updateAgentOutput.pending, (state) => {
        state.loading.updateAgentOutput = true;
        state.error = null;
      })
      .addCase(updateAgentOutput.fulfilled, (state, action) => {
        state.loading.updateAgentOutput = false;
      })
      .addCase(updateAgentOutput.rejected, (state, action) => {
        state.loading.updateAgentOutput = false;
        state.error = action.payload;
      })
      .addCase(fetchAC.pending, (state) => {
        state.loading.fetchAC = true;
        state.error = null;
      })
      .addCase(fetchAC.fulfilled, (state, action) => {
        state.loading.fetchAC = false;
        // Map API format to UI format
        if (action.payload?.data?.format === 'bullet points') {
          action.payload.data.format = 'bullet';
        }
        state.acceptanceCriteria = action.payload;;
      })
      .addCase(fetchAC.rejected, (state, action) => {
        state.loading.fetchAC = false;
        state.error = action.payload;
      })
      .addCase(createTickets.pending, (state) => {
        state.loading.createTickets = true;
        state.error = null;
      })
      .addCase(createTickets.fulfilled, (state, action) => {
        state.loading.createTickets = false;
      })
      .addCase(createTickets.rejected, (state, action) => {
        state.loading.createTickets = false;
        state.error = action.payload;
      })
      .addCase(createAC.pending, (state) => {
        state.loading.createAC = true;
        state.error = null;
      })
      .addCase(createAC.fulfilled, (state, action) => {
        state.loading.createAC = false;
      })
      .addCase(createAC.rejected, (state, action) => {
        state.loading.createAC = false;
        state.error = action.payload;
      })
      .addCase(fetchHistory.pending, (state) => {
        state.loading.fetchHistory = true;
        state.error = null;
      })
      .addCase(fetchHistory.fulfilled, (state, action) => {
        state.loading.fetchHistory = false;
        // Flatten the nested structure: data[].history[] -> flat array
        const flattenedData = [];
        if (action.payload.data && Array.isArray(action.payload.data)) {
          action.payload.data.forEach(project => {
            if (project.history && Array.isArray(project.history)) {
              project.history.forEach(historyItem => {
                // Map API format to UI format for history items
                if (historyItem.format === 'bullet points') {
                  historyItem.format = 'bullet';
                }
                flattenedData.push({
                  ...historyItem,
                  project_id: project.project_id,
                  project_name: project.project_name,
                });
              });
            }
          });
        }
        state.history.data = flattenedData;
        state.history.totalPages = action.payload.pagination?.total_pages || 1;
        state.history.totalItems = action.payload.pagination?.total_count || flattenedData.length;
        state.history.currentPage = action.payload.pagination?.current_page || 1;
      })
      .addCase(fetchHistory.rejected, (state, action) => {
        state.loading.fetchHistory = false;
        state.error = action.payload;
        state.history.data = [];
      })
      .addCase(fetchTaskGeneratorLatest.pending, (state) => {
        state.loading.fetchTaskGeneratorLatest = true;
      })
      .addCase(fetchTaskGeneratorLatest.fulfilled, (state, action) => {
        state.loading.fetchTaskGeneratorLatest = false;
        // Extract the first history item from the first project
        const firstProject = action.payload?.data?.[0];
        const firstItem = firstProject?.history?.[0] ?? null;
        state.taskGeneratorLatest = firstItem
          ? { ...firstItem, project_id: firstProject.project_id, project_name: firstProject.project_name }
          : null;
      })
      .addCase(fetchTaskGeneratorLatest.rejected, (state) => {
        state.loading.fetchTaskGeneratorLatest = false;
        state.taskGeneratorLatest = null;
      })
      .addCase(generateReport.pending, (state) => {
        state.loading.generateReport = true;
        state.error = null;
      })
      .addCase(generateReport.fulfilled, (state, action) => {
        state.loading.generateReport = false;
        const { projectId, reportType } = action.meta.arg;
        const reportKey = `${reportType}_${projectId}`;
        const p = action.payload;
        const generatedAt = p?.generated_at ?? p?.generatedAt ?? p?.data?.generated_at ?? p?.data?.generatedAt;
        state.reports[reportKey] = {
          ...p,
          reportType,
          projectId,
          generated_at: generatedAt,
          generatedAt: generatedAt,
        };
      })
      .addCase(generateReport.rejected, (state, action) => {
        state.loading.generateReport = false;
        state.error = action.payload;
      });
  },
});

export const { clearBreakdown, clearError, clearAC, clearHistory, clearReports } = strategistSlice.actions;
export default strategistSlice.reducer;
